import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { MessageCircle, Save, Send } from "lucide-react";

const EMPTY = { account_sid: "", auth_token: "", from_number: "", content_sid: "", message_template: "" };

export const WhatsAppSettingsCard = () => {
  const [doc, setDoc] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [enabled, setEnabled] = useState(false);
  const [testPhone, setTestPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState([]);

  const load = async () => {
    const r = await api.get("/whatsapp-settings");
    setDoc(r.data);
    setEnabled(!!r.data.alerts_enabled);
    setForm({
      account_sid: r.data.account_sid || "", auth_token: "", from_number: r.data.from_number || "",
      content_sid: r.data.content_sid || "", message_template: r.data.message_template || "",
    });
    api.get("/whatsapp/log").then((l) => setLog((l.data || []).filter((x) => ["milestone", "test"].includes(x.category)).slice(0, 8))).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setBusy(true);
    try {
      await api.put("/whatsapp-settings", patch);
      toast.success("WhatsApp settings saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const toggle = (v) => { setEnabled(v); save({ alerts_enabled: v }); };

  const sendTest = async () => {
    if (!testPhone.trim()) { toast.error("Enter a phone number"); return; }
    setBusy(true);
    try {
      await api.post("/whatsapp-settings/test", { phone: testPhone });
      toast.success("Test message sent");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
      load();
    } finally { setBusy(false); }
  };

  const F = (field, label, placeholder, type = "text") => (
    <div>
      <Label className="label-eyebrow">{label}</Label>
      <Input type={type} value={form[field]} placeholder={placeholder}
        onChange={(e) => setForm({ ...form, [field]: e.target.value })}
        className="rounded-none mt-1 h-10" data-testid={`wa-${field}`} />
    </div>
  );

  return (
    <section className="flat-card p-6 space-y-4" data-testid="gs-whatsapp-section">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="label-eyebrow flex items-center gap-2"><MessageCircle className="w-3.5 h-3.5" /> WhatsApp Milestone Alerts (Twilio)</div>
        <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest">
          {enabled ? "Alerts On" : "Alerts Off"}
          <Switch checked={enabled} onCheckedChange={toggle} disabled={busy || !doc} data-testid="wa-alerts-toggle" />
        </label>
      </div>
      <div className="text-[11px] text-[#4B5563]">
        When on, the customer gets a WhatsApp each time one of their 12 portal milestones is completed.
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {F("account_sid", "Account SID", "ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx")}
        {F("auth_token", "Auth Token", doc?.has_auth_token ? `Saved (${doc.auth_token_hint}) — leave blank to keep` : "Twilio auth token", "password")}
        {F("from_number", "WhatsApp Sender Number", "+14155238886")}
        {F("content_sid", "Approved Template SID (optional)", "HXxxxxxxxx — variables {{1}} customer, {{2}} milestone")}
      </div>
      <div>
        <Label className="label-eyebrow">Message Text</Label>
        <Textarea rows={3} value={form.message_template}
          onChange={(e) => setForm({ ...form, message_template: e.target.value })}
          className="rounded-none mt-1" data-testid="wa-message_template" />
        <div className="text-[11px] text-[#4B5563] mt-1">
          Placeholders: {"{customer_name}"}, {"{milestone}"}, {"{company_name}"}. Used when no template SID is set.
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <Button onClick={() => save(form)} disabled={busy} data-testid="wa-save"
          className="rounded-none bg-[#002FA7] hover:bg-[#002480] uppercase tracking-widest text-xs font-bold h-10">
          <Save className="w-4 h-4 mr-2" /> Save WhatsApp Settings
        </Button>
        <div className="flex items-end gap-2">
          <div>
            <Label className="label-eyebrow">Test Number</Label>
            <Input value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="9876543210"
              className="rounded-none mt-1 h-10 w-44" data-testid="wa-test-phone" />
          </div>
          <Button variant="outline" onClick={sendTest} disabled={busy} data-testid="wa-test-send"
            className="rounded-none uppercase tracking-widest text-xs font-bold h-10">
            <Send className="w-4 h-4 mr-2" /> Send Test
          </Button>
        </div>
      </div>
      {log.length > 0 && <RecentLog log={log} />}
    </section>
  );
};

const RecentLog = ({ log }) => (
  <div className="border border-[#E5E7EB]" data-testid="wa-recent-log">
    <div className="label-eyebrow px-3 py-2 bg-[#FAFAFA] border-b border-[#E5E7EB]">Recent Messages</div>
    {log.map((m) => (
      <div key={m.id} className="px-3 py-2 border-b border-[#F3F4F6] text-xs flex flex-wrap gap-x-3 gap-y-1" data-testid={`wa-log-${m.id}`}>
        <span className={`font-bold ${m.status === "SENT" ? "text-[#10B981]" : "text-[#DC2626]"}`}>{m.status}</span>
        <span className="font-mono">{m.recipient}</span>
        <span className="text-[#4B5563] truncate max-w-full">{m.error || m.message}</span>
        <span className="text-[#9CA3AF] ml-auto">{new Date(m.at).toLocaleString()}</span>
      </div>
    ))}
  </div>
);
